import { escapeHtml } from '../../../../shared/components/ai-report-view/ai-report-view.utilities';

interface ITextBlock {
  lines: string[];
  type: 'p' | 'ul';
}

function toHtmlBlock(block: ITextBlock): string {
  return block.type === 'ul'
    ? `<ul>${block.lines.map((line: string) => `<li>${line}</li>`).join('')}</ul>`
    : `<p>${block.lines.join('<br>')}</p>`;
}

/**
 * The agent answers in plain text (blank line = new paragraph, "- " = list item); the description editor wants HTML.
 * Every piece of text is escaped, so nothing in the answer can turn into markup.
 */
export function aiAssistTextToHtml(text: string): string {
  const blocks: ITextBlock[] = [];
  let isAfterBlankLine = true;

  for (const rawLine of text.split('\n')) {
    const line: string = rawLine.trim();

    if (!line) {
      isAfterBlankLine = true;

      continue;
    }

    const type: ITextBlock['type'] = line.startsWith('- ') ? 'ul' : 'p';
    const content: string = escapeHtml(type === 'ul' ? line.slice(2).trim() : line);
    const lastBlock: ITextBlock | undefined = blocks[blocks.length - 1];

    // List items stay in one list across blank lines; a paragraph ends at a blank line.
    if (lastBlock?.type === type && (type === 'ul' || !isAfterBlankLine)) {
      lastBlock.lines.push(content);
    } else {
      blocks.push({ lines: [content], type });
    }

    isAfterBlankLine = false;
  }

  return blocks.map(toHtmlBlock).join('');
}

/** Whether editor HTML shows any text; parsed inert (DOMParser runs no scripts and loads nothing). */
export function hasVisibleText(html: string | null): boolean {
  return Boolean(html && new DOMParser().parseFromString(html, 'text/html').body.textContent?.trim());
}
